// src/components/SimulaRosaExcelImport.js
// "Importa da Excel" in Simula rosa: stesso export "Esporta rose" di
// Fantacalcio.it accettato dal tab Lega dell'asta a listone (stesso parser,
// fantacalcioLeagueRosters.js). L'utente sceglie quale squadra del file è la
// sua; i giocatori vengono abbinati al dataset e importati con i crediti di
// acquisto indicati nel file (poi restano modificabili come sempre).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FileSpreadsheet, Loader2, X, Upload, AlertTriangle, Check } from 'lucide-react';
import { parseRosterFile, matchExcelTeamToPlayers } from '../utils/fantacalcioLeagueRosters';

const LAST_TEAM_KEY = 'fantapp_simula_excel_team';

function readLastTeam() {
  try { return localStorage.getItem(LAST_TEAM_KEY) || ''; } catch { return ''; }
}

function playerLabel(p) {
  return `${p.name} — ${p.team || 's.q.'}`;
}

export default function SimulaRosaExcelImport({ open, onClose, players = [], totalCredits = 0, hasSelection = false, onImport }) {
  const [parsed, setParsed] = useState(null);
  const [fileName, setFileName] = useState('');
  const [team, setTeam] = useState('');
  const [mode, setMode] = useState('replace');
  const [manual, setManual] = useState({}); // excelName → testo digitato (label "Nome — Squadra")
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const playerByLabel = useMemo(() => {
    const m = new Map();
    (players || []).forEach((p) => { if (p?.name) m.set(playerLabel(p), p); });
    return m;
  }, [players]);

  const match = useMemo(() => {
    if (!parsed || !team) return null;
    return matchExcelTeamToPlayers(parsed.teams[team] || [], players);
  }, [parsed, team, players]);

  // Proposte sicure (cognome + iniziale univoci, es. "Paz N." → Nico Paz)
  // pre-compilate: l'utente le vede nel campo e può cambiarle o svuotarle.
  useEffect(() => {
    if (!match) return;
    const guesses = {};
    match.unmatched.forEach((u) => { if (u.guess) guesses[u.name] = playerLabel(u.guess); });
    setManual(guesses);
  }, [match]);

  const resolvedManual = useMemo(() => {
    const out = [];
    (match?.unmatched || []).forEach((u) => {
      const p = playerByLabel.get(String(manual[u.name] || '').trim());
      if (p) out.push({ player: p, cost: u.cost, currentValue: u.currentValue });
    });
    return out;
  }, [match, manual, playerByLabel]);

  // credits = costo d'acquisto (col. D); currentValue = quotazione attuale
  // (col. E) se il file la riporta (rosa singola), altrimenti null → in
  // Simula rosa si usa la quotazione del listone.
  const importItems = useMemo(() => {
    const seen = new Set();
    return [...(match?.matched || []), ...resolvedManual].filter(({ player }) => {
      const id = String(player.id);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    }).map(({ player, cost, currentValue }) => ({
      playerId: String(player.id),
      credits: cost,
      currentValue: Number.isFinite(Number(currentValue)) && currentValue !== null ? Number(currentValue) : null,
    }));
  }, [match, resolvedManual]);

  const importTotal = importItems.reduce((acc, it) => acc + it.credits, 0);
  const hasCurrent = importItems.some((it) => it.currentValue !== null);
  const importCurrentTotal = importItems.reduce((acc, it) => acc + (it.currentValue ?? it.credits), 0);
  const skippedCount = (match?.unmatched.length || 0) - resolvedManual.length;

  if (!open) return null;

  const reset = () => {
    setParsed(null); setFileName(''); setTeam(''); setManual({}); setError(''); setMode('replace');
  };

  const close = () => { reset(); onClose(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      // Riconosce da solo sia l'export di tutta la lega sia la rosa singola (csv).
      const data = await parseRosterFile(file);
      if (!(data?.order || []).length) {
        setError('Non ho trovato rose nel file. Usa "Esporta rose" (tutta la lega) o l\'export della rosa della tua squadra di Fantacalcio.it.');
        return;
      }
      setParsed(data);
      setFileName(file.name);
      const last = readLastTeam();
      setTeam(data.order.length === 1 ? data.order[0] : (data.order.includes(last) ? last : ''));
    } catch (err) {
      setError('Impossibile leggere il file: ' + (err?.message || 'formato non riconosciuto') + '.');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const confirm = () => {
    try { localStorage.setItem(LAST_TEAM_KEY, team); } catch { /* ignore */ }
    onImport({ items: importItems, mode: hasSelection ? mode : 'replace' });
    close();
  };

  return (
    <div className="sr-modal-backdrop" onClick={close}>
      <div className="sr-modal sr-import glass" onClick={(e) => e.stopPropagation()}>
        <div className="sr-modal__head">
          <strong><FileSpreadsheet size={16} /> Importa la tua squadra da Excel</strong>
          <button type="button" className="sr-modal__close" onClick={close} aria-label="Chiudi">
            <X size={16} />
          </button>
        </div>

        {!parsed && (
          <>
            <p className="sr-modal__desc">
              Carica l'export <b>"Esporta rose"</b> di tutta la lega (Excel) oppure la <b>rosa della tua squadra</b>
              (CSV, es. <code>lega_squadra_rosa_data.csv</code>) di Fantacalcio.it. I giocatori vengono inseriti con i
              costi d'acquisto del file e, se c'è, con la quotazione attuale (il prezzo a cui li vendi oggi):
              entrambi restano modificabili.
            </p>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFile} hidden />
            <button type="button" className="sr-import__drop" onClick={() => fileRef.current?.click()} disabled={busy}>
              {busy ? <Loader2 size={18} className="spin" /> : <Upload size={18} />}
              {busy ? 'Leggo il file...' : 'Scegli il file (Excel o CSV)'}
            </button>
          </>
        )}

        {parsed && (
          <>
            <p className="sr-modal__desc">
              <b>{fileName}</b> · {parsed.kind === 'single'
                ? <>rosa di <b>{parsed.team}</b>, {(parsed.teams[parsed.team] || []).length} giocatori · {parsed.totals[parsed.team]} cr.</>
                : `${parsed.order.length} squadre.`}{' '}
              <button type="button" className="sr-import__link" onClick={reset}>Cambia file</button>
            </p>

            {parsed.kind !== 'single' && (
              <label className="sr-modal__field">
                <span>Qual è la tua squadra?</span>
                <select value={team} onChange={(e) => setTeam(e.target.value)}>
                  <option value="">— Scegli —</option>
                  {parsed.order.map((t) => (
                    <option key={t} value={t}>
                      {t} · {(parsed.teams[t] || []).length} giocatori{parsed.totals?.[t] ? ` · ${parsed.totals[t]} cr` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {match && (
              <div className="sr-import__summary">
                <span className="sr-import__ok"><Check size={14} /> {match.matched.length} giocatori riconosciuti</span>
                {match.unmatched.length > 0 && (
                  <span className="sr-import__warn"><AlertTriangle size={14} /> {match.unmatched.length} da abbinare a mano</span>
                )}
              </div>
            )}

            {match?.unmatched.length > 0 && (
              <div className="sr-import__unmatched">
                <p>
                  Questi nomi del file non corrispondono esattamente a un giocatore del listone. Dove ero sicuro
                  ho già proposto io il giocatore: controlla, cambia se serve, o svuota il campo per non importarlo.
                </p>
                <datalist id="sr-import-players">
                  {(players || []).map((p) => <option key={p.id} value={playerLabel(p)} />)}
                </datalist>
                {match.unmatched.map((u) => (
                  <div key={u.name} className="sr-import__row">
                    <div className="sr-import__excel">
                      <strong>{u.name}</strong>
                      <span>{u.cost} cr</span>
                    </div>
                    <input
                      type="text"
                      list="sr-import-players"
                      placeholder="Cerca giocatore..."
                      value={manual[u.name] || ''}
                      onChange={(e) => setManual((cur) => ({ ...cur, [u.name]: e.target.value }))}
                    />
                    {!manual[u.name] && u.suggestions.length > 0 && (
                      <div className="sr-import__suggest">
                        {u.suggestions.slice(0, 3).map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => setManual((cur) => ({ ...cur, [u.name]: playerLabel(s) }))}
                          >
                            {s.name} <em>{s.team}</em>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {match && hasSelection && (
              <div className="sr-import__mode">
                <label>
                  <input type="radio" checked={mode === 'replace'} onChange={() => setMode('replace')} />
                  Sostituisci la rosa attuale
                </label>
                <label>
                  <input type="radio" checked={mode === 'add'} onChange={() => setMode('add')} />
                  Aggiungi alla rosa attuale
                </label>
              </div>
            )}

            {match && (
              <p className="sr-import__total">
                Importo <b>{importItems.length}</b> giocatori: pagati <b>{importTotal}</b> crediti
                {hasCurrent && (
                  <>, valore attuale <b>{importCurrentTotal}</b> cr
                    {' '}(<b className={importCurrentTotal - importTotal >= 0 ? 'is-pos' : 'is-neg'}>
                      {importCurrentTotal - importTotal >= 0 ? '+' : ''}{importCurrentTotal - importTotal}
                    </b> se vendessi tutto oggi)</>
                )}
                {skippedCount > 0 ? ` (${skippedCount} non importati)` : ''}.
                {totalCredits > 0 && importTotal > totalCredits && (
                  <span className="sr-import__over"> Supera i {totalCredits} crediti impostati: puoi alzarli da "Modifica impostazioni".</span>
                )}
              </p>
            )}
          </>
        )}

        {error && <p className="sr-modal__error">{error}</p>}

        <div className="sr-modal__actions">
          <button type="button" className="sr-modal__cancel" onClick={close}>Annulla</button>
          <button type="button" className="sr-modal__confirm" disabled={!importItems.length} onClick={confirm}>
            Importa
          </button>
        </div>
      </div>
    </div>
  );
}
